/**
 * Data Loader Utility for Trading Decision Collection
 * Parses the full_states_log JSON file and extracts relevant data
 */

// Global cache for market prices
let priceDataCache: Map<string, { close: number; high: number; low: number; open: number; volume: number }> | null = null;

// Global cache for market index prices
let nasdaqDataCache: Map<string, { close: number; high: number; low: number; open: number; volume: number }> | null = null;
let dowDataCache: Map<string, { close: number; high: number; low: number; open: number; volume: number }> | null = null;
let sp500DataCache: Map<string, { close: number; high: number; low: number; open: number; volume: number }> | null = null;

export interface RawDayData {
  company_of_interest: string;
  trade_date: string;
  fundamentals_report: string;
  market_report: string;
  news_report: string;
  sentiment_report: string;
  portfolio_summary: any;
  investment_debate_state?: any;
  investment_plan?: string;
  final_trade_decision?: string;
  judge_decision?: string;
}

export interface CacheDay {
  date: string;
  ticker: string;
  fundamentals: {
    companyName?: string;
    marketCap?: string;
    pe?: number;
    eps?: number;
    revenueTTM?: string;
    grossMargin?: number;
    notes?: string[];
    notesMarkdown?: string;
    raw?: string;
  };
  market: {
    lastClose?: number;
    open?: number;
    high?: number;
    low?: number;
    volume?: number;
    indicators?: { sma20?: number; ema20?: number; rsi14?: number; macd?: number };
    raw?: string;
  };
  news: {
    items: { title: string; source?: string; time?: string; url?: string; summary?: string }[];
    raw?: string;
  };
  sentiment: {
    score?: number;
    analystRating?: string;
    social?: { platform: string; score: number; sample?: number }[];
    raw?: string;
  };
  price30d: { date: string; price: number; volume?: number; event?: string }[];
  nasdaq30d?: { date: string; price: number; volume?: number }[];
  dow30d?: { date: string; price: number; volume?: number }[];
  sp50030d?: { date: string; price: number; volume?: number }[];
}

function normalizeTitle(text: string) {
  return text
    .replace(/^\*\*(.+?)\*\*$/, "$1") // remove surrounding **
    .replace(/^\d+\.\s*/, "")        // remove leading "1. "
    .replace(/^\*\*(.+?)\*\*$/, "$1") // remove surrounding **
    .trim();
}

/**
 * Parse the fundamentals report to extract key metrics
 */
const TICKER_TO_NAME: Record<string, string> = {
  AAPL: "Apple Inc.",
  NVDA: "NVIDIA Corporation",
  GOOGL: "Alphabet Inc.",
};

function parseFundamentals(report: string, ticker: string): CacheDay['fundamentals'] {
  const fundamentals: CacheDay['fundamentals'] = {
    companyName: TICKER_TO_NAME[ticker] ?? ticker,
    raw: report,
    notes: []
  };

  // Extract metrics using regex patterns
  const revenueMatch = report.match(/Revenue.*?(\$[\d.]+\s*[BT]illion)/i);
  if (revenueMatch) {
    fundamentals.revenueTTM = revenueMatch[1];
  }

  const peMatch = report.match(/P\/E\s+Ratio.*?(\d+\.?\d*)/i);
  if (peMatch) {
    fundamentals.pe = parseFloat(peMatch[1]);
  }

  const epsMatch = report.match(/EPS.*?(\$?[\d.]+)/i);
  if (epsMatch) {
    fundamentals.eps = parseFloat(epsMatch[1].replace('$', ''));
  }

  const grossMarginMatch = report.match(/Gross\s+Margin.*?(\d+\.?\d*)%/i);
  if (grossMarginMatch) {
    fundamentals.grossMargin = parseFloat(grossMarginMatch[1]) / 100;
  }

  const marketCapMatch = report.match(/Market\s+Cap.*?(\$[\d.]+\s*[BT]illion)/i);
  if (marketCapMatch) {
    fundamentals.marketCap = marketCapMatch[1];
  }

  // Extract key points as notes
  const bulletPoints = report.match(/[-•]\s+(.+?)(?=\n|$)/g);
  if (bulletPoints) {
    fundamentals.notes = bulletPoints.slice(0, 5).map(p => p.replace(/^[-•]\s+/, '').trim());
  }

  return fundamentals;
}

/**
 * Parse the market report to extract technical indicators
 */
function parseMarket(report: string): CacheDay['market'] {
  const market: CacheDay['market'] = {
    raw: report,
    indicators: {}
  };

  // Extract price data
  const closeMatch = report.match(/(?:Close|closing price).*?(\d+\.?\d*)/i);
  if (closeMatch) {
    market.lastClose = Number(parseFloat(closeMatch[1]).toFixed(2));
  }

  const openMatch = report.match(/Open.*?(\d+\.?\d*)/i);
  if (openMatch) {
    market.open = Number(parseFloat(openMatch[1]).toFixed(2));
  }

  const highMatch = report.match(/High.*?(\d+\.?\d*)/i);
  if (highMatch) {
    market.high = Number(parseFloat(highMatch[1]).toFixed(2));
  }

  const lowMatch = report.match(/Low.*?(\d+\.?\d*)/i);
  if (lowMatch) {
    market.low = Number(parseFloat(lowMatch[1]).toFixed(2));
  }

  const volumeMatch = report.match(/Volume.*?([\d,]+)/i);
  if (volumeMatch) {
    market.volume = parseInt(volumeMatch[1].replace(/,/g, ''));
  }

  return market;
}

/**
 * Parse the news report to extract news items
 */
function parseNews(report: string): CacheDay['news'] {
  const news: CacheDay['news'] = {
    items: [],
    raw: report
  };

  // Try to extract structured news items
  // Look for patterns like headers, bullet points, or numbered lists
  const sections = report.split(/\n\s*\n/);

  for (const section of sections) {
    // Try to find title-like patterns
    const titleMatch =
      section.match(/^#+\s*(.+)$/m) ||
      section.match(/^\*\*(.+?)\*\*/m);
  
    if (titleMatch) {
      const title = normalizeTitle(titleMatch[1]);
  
      // Remove title line from section
      const body = section.replace(titleMatch[0], "").trim();
  
      // Convert body to markdown-friendly summary
      const summaryMarkdown = body
        .split(/\n+/)
        .filter(line => line.trim().length > 0)
        .slice(0, 6) // limit number of lines instead of chars
        .map(line => `- ${line.trim()}`)
        .join("\n");
  
      news.items.push({
        title,
        summary: summaryMarkdown || undefined
      });
    }
  }  

  // If no structured items found, create summary items from paragraphs
  if (news.items.length === 0) {
    const paragraphs = report.split(/\n\s*\n/).filter(p => p.trim().length > 50);
    news.items = paragraphs.slice(0, 5).map((p, i) => ({
      title: `News Item ${i + 1}`,
      summary: p.trim().slice(0, 400)
    }));
  }

  return news;
}

/**
 * Parse the sentiment report to extract sentiment data
 */
function parseSentiment(report: string): CacheDay['sentiment'] {
  const sentiment: CacheDay['sentiment'] = {
    raw: report,
    social: []
  };

  // Extract sentiment score
  const scoreMatch = report.match(/sentiment\s+score.*?([-]?\d+\.?\d*)/i);
  if (scoreMatch) {
    sentiment.score = parseFloat(scoreMatch[1]);
  }

  // Extract analyst rating
  const ratingMatch = report.match(/analyst\s+rating.*?:\s*(.+?)(?:\n|$)/i);
  if (ratingMatch) {
    sentiment.analystRating = ratingMatch[1].trim();
  }

  // Extract social media mentions
  const twitterMatch = report.match(/Twitter|X.*?(?:score|sentiment).*?([-]?\d+\.?\d*)/i);
  if (twitterMatch) {
    sentiment.social!.push({
      platform: 'X (Twitter)',
      score: parseFloat(twitterMatch[1]),
      sample: 1000
    });
  }

  const redditMatch = report.match(/Reddit.*?(?:score|sentiment).*?([-]?\d+\.?\d*)/i);
  if (redditMatch) {
    sentiment.social!.push({
      platform: 'Reddit',
      score: parseFloat(redditMatch[1]),
      sample: 500
    });
  }

  return sentiment;
}

/**
 * Load market price data from CSV
 */
async function loadMarketPrices(): Promise<Map<string, { close: number; high: number; low: number; open: number; volume: number }>> {
  if (priceDataCache) {
    return priceDataCache;
  }

  try {
    const response = await fetch('/gpt_4o_mini/aapl/market_prices.csv');
    const csvText = await response.text();
    const lines = csvText.split('\n');
    const priceMap = new Map<string, { close: number; high: number; low: number; open: number; volume: number }>();

    // Skip header line
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const [date, close, high, low, open, volume] = line.split(',');
      priceMap.set(date, {
        close: Number(parseFloat(close).toFixed(2)),
        high: Number(parseFloat(high).toFixed(2)),
        low: Number(parseFloat(low).toFixed(2)),
        open: Number(parseFloat(open).toFixed(2)),
        volume: parseFloat(volume)
      });
    }

    priceDataCache = priceMap;
    return priceMap;
  } catch (error) {
    console.error('Failed to load market prices:', error);
    return new Map();
  }
}

/**
 * Load Nasdaq index data from CSV
 */
async function loadNasdaqPrices(): Promise<Map<string, { close: number; high: number; low: number; open: number; volume: number }>> {
  if (nasdaqDataCache) {
    return nasdaqDataCache;
  }

  try {
    const response = await fetch('/us_market_indices/Nasdaq.csv');
    const csvText = await response.text();
    const lines = csvText.split('\n');
    const priceMap = new Map<string, { close: number; high: number; low: number; open: number; volume: number }>();

    // Skip first two lines (header and ticker row)
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const [date, close, high, low, open, volume] = line.split(',');
      priceMap.set(date, {
        close: Number(parseFloat(close).toFixed(2)),
        high: Number(parseFloat(high).toFixed(2)),
        low: Number(parseFloat(low).toFixed(2)),
        open: Number(parseFloat(open).toFixed(2)),
        volume: parseFloat(volume)
      });
    }

    nasdaqDataCache = priceMap;
    return priceMap;
  } catch (error) {
    console.error('Failed to load Nasdaq prices:', error);
    return new Map();
  }
}

/**
 * Load Dow Jones index data from CSV
 */
async function loadDowPrices(): Promise<Map<string, { close: number; high: number; low: number; open: number; volume: number }>> {
  if (dowDataCache) {
    return dowDataCache;
  }

  try {
    const response = await fetch('/us_market_indices/Dow.csv');
    const csvText = await response.text();
    const lines = csvText.split('\n');
    const priceMap = new Map<string, { close: number; high: number; low: number; open: number; volume: number }>();

    // Skip first two lines (header and ticker row)
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const [date, close, high, low, open, volume] = line.split(',');
      priceMap.set(date, {
        close: Number(parseFloat(close).toFixed(2)),
        high: Number(parseFloat(high).toFixed(2)),
        low: Number(parseFloat(low).toFixed(2)),
        open: Number(parseFloat(open).toFixed(2)),
        volume: parseFloat(volume)
      });
    }

    dowDataCache = priceMap;
    return priceMap;
  } catch (error) {
    console.error('Failed to load Dow prices:', error);
    return new Map();
  }
}

/**
 * Load S&P 500 index data from CSV
 */
async function loadSP500Prices(): Promise<Map<string, { close: number; high: number; low: number; open: number; volume: number }>> {
  if (sp500DataCache) {
    return sp500DataCache;
  }

  try {
    const response = await fetch('/us_market_indices/S&P_500.csv');
    const csvText = await response.text();
    const lines = csvText.split('\n');
    const priceMap = new Map<string, { close: number; high: number; low: number; open: number; volume: number }>();

    // Skip first two lines (header and ticker row)
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const [date, close, high, low, open, volume] = line.split(',');
      priceMap.set(date, {
        close: Number(parseFloat(close).toFixed(2)),
        high: Number(parseFloat(high).toFixed(2)),
        low: Number(parseFloat(low).toFixed(2)),
        open: Number(parseFloat(open).toFixed(2)),
        volume: parseFloat(volume)
      });
    }

    sp500DataCache = priceMap;
    return priceMap;
  } catch (error) {
    console.error('Failed to load S&P 500 prices:', error);
    return new Map();
  }
}

/**
 * Get price history for 30 days before the given date using real market data
 */
async function getPriceHistory(date: string, priceData: Map<string, any>): Promise<CacheDay['price30d']> {
  const price30d: CacheDay['price30d'] = [];
  const endDate = new Date(date);

  for (let i = 29; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);

    const priceInfo = priceData.get(dateStr);
    if (priceInfo) {
      price30d.push({
        date: dateStr,
        price: priceInfo.close,
        volume: priceInfo.volume
      });
    }
  }

  // If we don't have 30 days of data, fill in with mock data
  if (price30d.length === 0) {
    // Fallback to mock data
    const mockPrice = 180; // Default price
    for (let i = 29; i >= 0; i--) {
      const d = new Date(endDate);
      d.setDate(d.getDate() - i);
      const variation = (Math.random() - 0.5) * 10;
      const price = mockPrice + variation - (i * 0.5);

      price30d.push({
        date: d.toISOString().slice(0, 10),
        price: Math.round(price * 100) / 100,
        volume: Math.floor(40000000 + Math.random() * 20000000)
      });
    }
  }

  return price30d;
}

/**
 * Get Nasdaq index history for 30 days before the given date using real market data
 */
async function getNasdaqHistory(date: string, nasdaqData: Map<string, any>): Promise<CacheDay['nasdaq30d']> {
  const nasdaq30d: CacheDay['nasdaq30d'] = [];
  const endDate = new Date(date);

  for (let i = 29; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);

    const priceInfo = nasdaqData.get(dateStr);
    if (priceInfo) {
      nasdaq30d.push({
        date: dateStr,
        price: priceInfo.close,
        volume: priceInfo.volume
      });
    }
  }

  return nasdaq30d;
}

/**
 * Get Dow Jones index history for 30 days before the given date using real market data
 */
async function getDowHistory(date: string, dowData: Map<string, any>): Promise<CacheDay['dow30d']> {
  const dow30d: CacheDay['dow30d'] = [];
  const endDate = new Date(date);

  for (let i = 29; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);

    const priceInfo = dowData.get(dateStr);
    if (priceInfo) {
      dow30d.push({
        date: dateStr,
        price: priceInfo.close,
        volume: priceInfo.volume
      });
    }
  }

  return dow30d;
}

/**
 * Get S&P 500 index history for 30 days before the given date using real market data
 */
async function getSP500History(date: string, sp500Data: Map<string, any>): Promise<CacheDay['sp50030d']> {
  const sp50030d: CacheDay['sp50030d'] = [];
  const endDate = new Date(date);

  for (let i = 29; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);

    const priceInfo = sp500Data.get(dateStr);
    if (priceInfo) {
      sp50030d.push({
        date: dateStr,
        price: priceInfo.close,
        volume: priceInfo.volume
      });
    }
  }

  return sp50030d;
}

/**
 * Transform raw day data into CacheDay format
 */
export async function transformDayData(
  rawData: RawDayData,
  priceData: Map<string, any>,
  nasdaqData: Map<string, any>,
  dowData: Map<string, any>,
  sp500Data: Map<string, any>
): Promise<CacheDay> {
  const fundamentals = parseFundamentals(rawData.fundamentals_report, rawData.company_of_interest);
  const market = parseMarket(rawData.market_report);
  const news = parseNews(rawData.news_report);
  const sentiment = parseSentiment(rawData.sentiment_report);

  // Get real price data for this date
  const datePrice = priceData.get(rawData.trade_date);

  // Update market data with real prices if available
  if (datePrice) {
    market.lastClose = datePrice.close;
    market.open = datePrice.open;
    market.high = datePrice.high;
    market.low = datePrice.low;
    market.volume = Math.round(datePrice.volume);
  }

  // Get 30-day price history using real data
  const price30d = await getPriceHistory(rawData.trade_date, priceData);

  // Get 30-day index histories using real data
  const nasdaq30d = await getNasdaqHistory(rawData.trade_date, nasdaqData);
  const dow30d = await getDowHistory(rawData.trade_date, dowData);
  const sp50030d = await getSP500History(rawData.trade_date, sp500Data);

  return {
    date: rawData.trade_date,
    ticker: rawData.company_of_interest,
    fundamentals,
    market,
    news,
    sentiment,
    price30d,
    nasdaq30d,
    dow30d,
    sp50030d
  };
}

/**
 * Load and parse the full states log JSON file
 */
export async function loadTradingData(filePath: string): Promise<Map<string, CacheDay>> {
  // Load market prices and all index data
  const priceData = await loadMarketPrices();
  const nasdaqData = await loadNasdaqPrices();
  const dowData = await loadDowPrices();
  const sp500Data = await loadSP500Prices();

  const response = await fetch(filePath);
  const rawData: Record<string, RawDayData> = await response.json();

  const tradingData = new Map<string, CacheDay>();

  for (const [date, dayData] of Object.entries(rawData)) {
    const cacheDay = await transformDayData(dayData, priceData, nasdaqData, dowData, sp500Data);
    tradingData.set(date, cacheDay);
  }

  return tradingData;
}

/**
 * Get sorted list of available dates
 */
export function getAvailableDates(tradingData: Map<string, CacheDay>): string[] {
  return Array.from(tradingData.keys()).sort();
}
